/**
 * Type-safe route builder functions for React Router
 * These functions generate route paths with proper parameter interpolation
 */

import type {
  RouteParams,
  WorkspaceRouteParams,
  ProjectRouteParams,
  TaskRouteParams,
} from "../types/routes";

/**
 * Route builder object containing type-safe path generation functions
 * Use these functions to generate route paths instead of hardcoding strings
 * 
 * @example
 * ```ts
 * // Navigate to a workspace
 * navigate(routes.workspace('ws-123'));
 * 
 * // Navigate to a project
 * navigate(routes.project('ws-123', 'proj-456'));
 * ```
 */
export const routes = {
  /**
   * Root path - redirects to workspaces
   */
  root: (): string => "/",

  /**
   * Login page
   */
  login: (): string => "/login",

  /**
   * Workspaces list page
   */
  workspaces: (): string => "/workspaces",

  /**
   * Specific workspace detail page
   */
  workspace: (workspaceId: string): string => `/workspaces/${workspaceId}`,

  /**
   * Projects list within a workspace
   */
  projects: (workspaceId: string): string => `/workspaces/${workspaceId}/projects`,

  /**
   * Specific project detail page (includes dashboard)
   */
  project: (workspaceId: string, projectId: string): string =>
    `/workspaces/${workspaceId}/projects/${projectId}`,

  /**
   * Tasks within a specific project
   */
  projectTasks: (workspaceId: string, projectId: string): string =>
    `/workspaces/${workspaceId}/projects/${projectId}/tasks`,

  /**
   * Specific task detail page
   */
  task: (workspaceId: string, projectId: string, taskId: string): string =>
    `/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}`,

  /**
   * Files associated with a specific task
   */
  taskFiles: (workspaceId: string, projectId: string, taskId: string): string =>
    `/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/files`,

  /**
   * All tasks within a workspace (not project-specific)
   */
  workspaceTasks: (workspaceId: string): string => `/workspaces/${workspaceId}/tasks`,

  /**
   * All files within a workspace (not task-specific)
   */
  workspaceFiles: (workspaceId: string): string => `/workspaces/${workspaceId}/files`,

  /**
   * Notifications for a workspace
   */
  notifications: (workspaceId: string): string => `/workspaces/${workspaceId}/notifications`,

  /**
   * Ventures list within a workspace
   */
  ventures: (workspaceId: string): string => `/workspaces/${workspaceId}/ventures`,

  /**
   * Specific venture detail page
   */
  ventureDetail: (workspaceId: string, ventureId: string): string =>
    `/workspaces/${workspaceId}/ventures/${ventureId}`,

  /**
   * Create new venture page
   */
  createVenture: (workspaceId: string): string => `/workspaces/${workspaceId}/ventures/new`,

  /**
   * Edit venture page
   */
  editVenture: (workspaceId: string, ventureId: string): string =>
    `/workspaces/${workspaceId}/ventures/${ventureId}/edit`,

  /**
   * My pledges page (user-scoped, not workspace-scoped)
   */
  myPledges: (): string => `/pledges`,
} as const;

// Re-export types for convenience
export type {
  RouteParams,
  WorkspaceRouteParams,
  ProjectRouteParams,
  TaskRouteParams,
};
